/* istanbul ignore file */ /* this file is not used or will be deleted in other branches */
// @ts-nocheck
import React, { useMemo, useCallback, useEffect } from 'react';
import { useForm, FormProvider, UseFormReturn } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { Button } from '@td-client-frontend/common';
import { TabsWithPerms } from '../../../components/TabsWithPerms';
import { validationSchema } from '../../../utils/members/validationSchema';
import Modal, { ModalFooterButtonGroup } from '../../../components/Modal/NewModal';
import { useMemberContext } from '../../../context/MemberContext';
import AccountInformation from './AccountInformation';
import ContactInformation from './ContactInformation';
import DependantsInformation from './DependantsInformation';
import WithPermission, { PermMode } from '../../../components/WithPermission';
import { MemberDetails } from '../../../@types/Members';
import { Permissions } from '../../../@types/Permissions';
import useValidateDupeMember from '../../../hooks/UseValidateDupeMember';
import { WarningModalContent } from './WarningModalContent';

export enum MemberTabs {
  Account = 'Account information',
  Contact = 'Contact information',
  Dependents = 'Dependents'
}

interface Props {
  defaultValues: any;
  isCreate: boolean;
  isLoading?: boolean;
  onCancel: (reset: boolean) => void;
  fetchMembers: ({ search, skip, limit }: any) => Promise<any>;

  onSave: (
    data: any,
    isPrimaryAction: boolean,
    isCreateAction: boolean,
    originMember: MemberDetails,
    setFormValue: (key: string, value: any) => void
  ) => void;
}

const PERMS_FOR_TABS = {
  perms: [Permissions.MemberRead, Permissions.MemberWrite, Permissions.MemberCreate],
  permsMode: PermMode.ANY
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function MemberFormContent({
  defaultValues,
  isCreate,
  onCancel,
  onSave,
  isLoading,
  fetchMembers
}: Props) {
  const methods: UseFormReturn = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: joiResolver(validationSchema)
  });
  const {
    watch,
    setValue,
    formState: { isValid }
  } = methods;

  const { showWarningModal, setShowWarningModal, userDetails } = useValidateDupeMember(watch, fetchMembers, isCreate);

  const {
    activeTab,
    setActiveTab,
    dependentId,
    isCreateDependent,
    handleClear,
    dependentForm,
    isViewMode,
    isDependentFormValid
  } = useMemberContext();
  const readOnly: boolean = watch('readOnly');

  useEffect(() => {
    setValue('readOnly', isViewMode);
  }, [isViewMode, setValue]);

  const isPrimaryAction: boolean = useMemo(() => !(dependentId || isCreateDependent), [dependentId, isCreateDependent]);

  const isSaveEnabled = useMemo(() => {
    return isPrimaryAction ? isValid : isDependentFormValid;
  }, [isPrimaryAction, isValid, isDependentFormValid]);

  const handleCancel = useCallback(() => {
    // i want to get rid of this but i need to understand how its working first so i can handle it better

    // what this is supposed to do... is clear the dependent form, but this is confusing
    // this code clears all the form info when on the dependent account form...
    if (isPrimaryAction) {
      onCancel(false);
    } else {
      // in an attempt to exist the dependent form...does it make more sense to just clear the dependentId? and send them back to the dependents tab
      // in my testing for some reason the real trigger for this is the "iscreate" flag, as soon as that changes it re-renders the component. explicitly setting the contact tab doesn't switch the tab...
      handleClear();
    }
  }, [isPrimaryAction, onCancel, handleClear]);

  const handleSave = useCallback(async () => {
    let source: UseFormReturn;
    let isCreateAction: boolean;

    if (isPrimaryAction) {
      source = methods;
      isCreateAction = isCreate;
    } else {
      source = dependentForm;
      isCreateAction = isCreateDependent;
    }

    const { getValues } = source;

    if (isSaveEnabled) {
      const data = getValues();
      delete data.originMember?.primaryId;
      onSave(data, isPrimaryAction, isCreateAction, methods.getValues('originMember'), methods.setValue);
    }
  }, [isPrimaryAction, isSaveEnabled, methods, isCreate, dependentForm, isCreateDependent, onSave]);

  const handleTabChange = useCallback((tab: string) => setActiveTab(tab), [setActiveTab]);

  const tabsSettings: TabSettings = useMemo(() => {
    const result: TabSettings = {
      [MemberTabs.Account]: {
        ...PERMS_FOR_TABS,
        label: MemberTabs.Account,
        component: <AccountInformation isCreate={isCreate} />
      },
      [MemberTabs.Contact]: {
        ...PERMS_FOR_TABS,
        label: MemberTabs.Contact,
        component: <ContactInformation />
      },
      [MemberTabs.Dependents]: {
        ...PERMS_FOR_TABS,
        label: MemberTabs.Dependents,
        component: <DependantsInformation readOnly={readOnly} />
      }
    };
    if (isCreate) {
      delete result[MemberTabs.Dependents];
    }
    return result;
  }, [isCreate, readOnly]);

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col w-full h-full">
        <form className="px-8 flex-1 overflow-y-auto">
          <TabsWithPerms onTabChange={handleTabChange} tabsSettings={tabsSettings} selectedTab={activeTab} />
        </form>
        {!readOnly && (
          <WithPermission requiredPerms={[Permissions.MemberCreate, Permissions.MemberWrite]} mode={PermMode.ANY}>
            <ModalFooterButtonGroup>
              <div>
                <Button onClick={handleCancel} kind="secondary" className="rounded-3xl px-6">
                  Cancel
                </Button>
              </div>
              <div>
                <Button onClick={handleSave} disabled={!isSaveEnabled || isLoading} className="rounded-3xl px-6">
                  Save
                </Button>
              </div>
            </ModalFooterButtonGroup>
          </WithPermission>
        )}
        <Modal
          isOpen={showWarningModal}
          onClose={() => setShowWarningModal(false)}
          header={<span style={{ color: 'red' }}>WARNING!</span>}
          style={{ color: 'red' }}
          actions={[
            {
              title: 'Close',
              onClick: () => setShowWarningModal(false),
              autoClose: true
            }
          ]}
          size="medium"
        >
          <WarningModalContent userDetails={userDetails} />
        </Modal>
      </div>
    </FormProvider>
  );
}
